import React from 'react';
import { useTranslation } from 'react-i18next';

const ConfirmationDialog = ({ 
  isOpen, 
  onClose, 
  onConfirm, 
  type, 
  rejectReason, 
  setRejectReason 
}) => {
  const { t } = useTranslation();
  if (!isOpen) return null;

  const handleConfirm = () => {
    if (type === 'reject' && !rejectReason.trim()) return;
    onConfirm();
  };

  const getDialogConfig = () => {
    switch (type) {
      case 'cancel':
        return {
          title: 'Cancel Competition Request',
          message: 'Are you sure you want to cancel this competition request? This action cannot be undone and the other organization will be notified.',
          buttonText: 'Cancel Request',
          buttonClass: 'bg-red-600 hover:bg-red-700 focus:ring-red-500',
          iconClass: 'bg-red-100',
          iconColor: 'text-red-600'
        };
      case 'accept':
        return {
          title: 'Accept Competition Request',
          message: 'Are you sure you want to accept this competition request? This will create a scheduled match between your teams.',
          buttonText: 'Accept Request',
          buttonClass: 'bg-green-600 hover:bg-green-700 focus:ring-green-500',
          iconClass: 'bg-green-100',
          iconColor: 'text-green-600'
        };
      case 'reject':
        return {
          title: 'Reject Competition Request',
          message: 'Are you sure you want to reject this competition request? Please provide a reason for rejection.',
          buttonText: 'Reject Request',
          buttonClass: 'bg-red-600 hover:bg-red-700 focus:ring-red-500',
          iconClass: 'bg-red-100',
          iconColor: 'text-red-600'
        };
      default:
        return {};
    }
  };

  const config = getDialogConfig();

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={onClose}></div>

        <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>

        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="sm:flex sm:items-start">
              <div className={`mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full ${config.iconClass} sm:mx-0 sm:h-10 sm:w-10`}>
                {type === 'accept' ? (
                  <svg className={`h-6 w-6 ${config.iconColor}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                ) : (
                  <svg className={`h-6 w-6 ${config.iconColor}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                )}
              </div>
              <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                <h3 className="text-lg leading-6 font-medium text-gray-900">
                  {config.title}
                </h3>
                <div className="mt-2">
                  <p className="text-sm text-gray-500 mb-3">
                    {config.message}
                  </p>
                  {type === 'reject' && (
                    <textarea
                      value={rejectReason}
                      onChange={(e) => setRejectReason(e.target.value)}
                      placeholder={t("buttons.enterReasonForRejection")}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                      rows={3}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <button
              type="button"
              onClick={handleConfirm}
              disabled={type === 'reject' && !rejectReason.trim()}
              className={`w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 text-base font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 sm:ml-3 sm:w-auto sm:text-sm ${config.buttonClass} ${
                type === 'reject' && !rejectReason.trim() ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              {config.buttonText}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfirmationDialog; 